import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import ProductCard from "./ProductCard";
import ProductDetailModal from "./ProductDetailModal";
import { fetchRawCatalogData } from "../../services/catalogService";
import { getCampaignThemeStyles } from "../../utils/campaignThemes";

interface CampaignItem {
  id: string; // config_campaign or config_campaign_2
  title: string;
  description: string;
  emoji: string;
  theme: string;
  styles: any;
  products: any[];
}

export const CatalogoEspecialTemporada: React.FC = () => {
  const [activeCampaigns, setActiveCampaigns] = useState<CampaignItem[]>([]);
  const [selectedCampaignTab, setSelectedCampaignTab] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);

  const loadCampaignsAndProducts = async () => {
    try {
      setLoading(true);

      // 1. Fetch desduplicado y cacheado de base de datos
      const data = await fetchRawCatalogData();

      // Buscar configuración de Campaña 1 y Campaña 2
      const configRow1 = data.find((row: any) => row.ID === "config_campaign");
      const configRow2 = data.find((row: any) => row.ID === "config_campaign_2");

      const checkActive = (row: any) =>
        Boolean(
          row &&
            (String(row.Disponible).toUpperCase() === "TRUE" ||
              String(row["Porciones/Unidades"]).toLowerCase() === "active" ||
              String(row["Porciones/Unidades"]).toUpperCase() === "TRUE")
        );

      const isCampaign1Active = checkActive(configRow1);
      const isCampaign2Active = checkActive(configRow2);

      // Helper para procesar y agrupar productos por categoría
      const processProductsForCategory = (filterFn: (cat: string) => boolean) => {
        const rows = data.filter((row: any) => {
          if (
            !row ||
            !row.ID ||
            row.ID.startsWith("config_")
          )
            return false;
          const cat = (row.Categoría || "").trim().toLowerCase();
          const isMatched = filterFn(cat);
          const isAvailable =
            row.Disponible === undefined ||
            row.Disponible === null ||
            row.Disponible.toString().toUpperCase() !== "FALSE";
          return isMatched && isAvailable;
        });

        const productsMap = new Map<string, any>();
        rows.forEach((row: any) => {
          const id = row.ID;
          if (!id) return;

          const price =
            parseFloat((row["Precio ($)"] || "").replace("$", "").replace(",", ".")) || 0;

          if (productsMap.has(id)) {
            const prod = productsMap.get(id);
            prod.prices.push({ people: row["Porciones/Unidades"], price });
          } else {
            const imageUrls = row.Imagen
              ? row.Imagen.split(",").map((s: string) => s.trim()).filter(Boolean)
              : [];

            productsMap.set(id, {
              id,
              name: row.Nombre || "Torta Especial",
              category: "edicion-especial",
              emoji: row.Emoji || "✨",
              image: imageUrls[0] || "",
              images: imageUrls,
              description: row.Descripción || "",
              prices: [{ people: row["Porciones/Unidades"], price }],
              available: true,
              badge: row.Badge || "",
              orden:
                row.Orden !== undefined && row.Orden !== null
                  ? Number(row.Orden)
                  : undefined,
            });
          }
        });

        return Array.from(productsMap.values()).sort((a: any, b: any) => {
          const hasOrdenA = a.orden !== undefined && a.orden !== null;
          const hasOrdenB = b.orden !== undefined && b.orden !== null;
          if (hasOrdenA && hasOrdenB) return a.orden! - b.orden!;
          if (hasOrdenA) return -1;
          if (hasOrdenB) return 1;
          return a.name.localeCompare(b.name);
        });
      };

      // Productos de Campaña 1: "edición especial", "edicion especial", "especial", "temporada"
      const campaign1Products = processProductsForCategory((cat) => {
        return (
          cat === "edición especial" ||
          cat === "edicion especial" ||
          cat === "especial" ||
          cat === "temporada" ||
          cat === "edicion-especial" ||
          cat === "edición especial (campaña 1)"
        );
      });

      // Productos de Campaña 2: "edición especial (campaña 2)", "edicion especial 2", "edicion-especial-2", "temporada 2"
      const campaign2Products = processProductsForCategory((cat) => {
        return (
          cat === "edición especial (campaña 2)" ||
          cat === "edicion especial (campaña 2)" ||
          cat === "edición especial 2" ||
          cat === "edicion especial 2" ||
          cat === "edicion-especial-2" ||
          cat === "temporada 2"
        );
      });

      const campaignsList: CampaignItem[] = [];

      if (configRow1 && isCampaign1Active) {
        const theme = configRow1.Badge || "madres";
        campaignsList.push({
          id: "config_campaign",
          title: configRow1.Nombre || "Catálogo Especial",
          description: configRow1.Descripción || "",
          emoji: configRow1.Emoji || "🌸",
          theme,
          styles: getCampaignThemeStyles(theme),
          products: campaign1Products,
        });
      }

      if (configRow2 && isCampaign2Active) {
        const theme = configRow2.Badge || "mundial";
        campaignsList.push({
          id: "config_campaign_2",
          title: configRow2.Nombre || "Segunda Campaña Especial",
          description: configRow2.Descripción || "",
          emoji: configRow2.Emoji || "✨",
          theme,
          styles: getCampaignThemeStyles(theme),
          products: campaign2Products,
        });
      }

      setActiveCampaigns(campaignsList);
    } catch (err) {
      console.error("Error al cargar campañas de temporada:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaignsAndProducts();
  }, []);

  if (loading || activeCampaigns.length === 0) {
    return null;
  }

  const currentCamp = activeCampaigns[selectedCampaignTab] || activeCampaigns[0];
  const { styles } = currentCamp;

  const handleOpenDetail = (product: any) => {
    setSelectedProduct(product);
    window.history.replaceState(null, "", `#postre-${product.id}`);
  };

  const handleCloseDetail = () => {
    setSelectedProduct(null);
    if (
      window.location.hash.startsWith("#postre-") ||
      window.location.hash.startsWith("#detail-")
    ) {
      window.history.replaceState(null, "", "#catalogo");
    }
  };

  const getGridContainerClass = (count: number) => {
    if (count === 1) return "flex justify-center max-w-sm mx-auto";
    if (count === 2)
      return "grid grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto gap-5";
    if (count === 3)
      return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto gap-5";
    return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5";
  };

  return (
    <>
      <section
        id="special-campaign-catalog"
        className="py-16 md:py-24 transition-all duration-500 border-b border-rose-100/60"
        style={{ background: styles.bg }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {/* Selector de Pestañas si hay 2 Campañas Activas */}
          {activeCampaigns.length > 1 && (
            <div className="flex justify-center mb-8">
              <div className="inline-flex p-1.5 rounded-2xl bg-white/80 backdrop-blur-md border border-rose-200 shadow-sm gap-2">
                {activeCampaigns.map((camp, idx) => (
                  <button
                    key={camp.id}
                    onClick={() => setSelectedCampaignTab(idx)}
                    className={`px-5 py-2.5 rounded-xl font-body text-xs font-bold transition-all flex items-center gap-2 ${
                      selectedCampaignTab === idx
                        ? "bg-rose-700 text-white shadow-md shadow-rose-900/10 scale-[1.02]"
                        : "text-rose-900/70 hover:text-rose-950 hover:bg-rose-50"
                    }`}
                  >
                    <span>{camp.emoji}</span>
                    <span>{camp.title}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Header de la Campaña Actual */}
          <div className="text-center mb-10 reveal">
            <p
              className={`font-body text-xs ${styles.labelColor} tracking-[0.3em] uppercase font-medium mb-3`}
            >
              Catálogo de Temporada {currentCamp.emoji}
            </p>
            <h2
              className={`font-display text-4xl md:text-5xl font-semibold ${styles.textColor} mb-4`}
            >
              {currentCamp.title}
            </h2>
            {currentCamp.description && (
              <p
                className={`font-body ${styles.subColor} text-base max-w-lg mx-auto whitespace-pre-line`}
              >
                {currentCamp.description}
              </p>
            )}
          </div>

          {/* Grilla adaptativa y centrada */}
          {currentCamp.products.length > 0 ? (
            <div className={getGridContainerClass(currentCamp.products.length)}>
              {currentCamp.products.map((product) => (
                <div key={product.id} className="w-full">
                  <ProductCard product={product} onOpenDetail={handleOpenDetail} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-sm text-gray-500 bg-white/40 rounded-3xl border border-dashed border-rose-200/60 max-w-lg mx-auto">
              Pronto agregaremos deliciosas creaciones para este catálogo especial.
            </div>
          )}
        </div>
      </section>

      {/* Modal renderizado en portal */}
      {selectedProduct &&
        ReactDOM.createPortal(
          <ProductDetailModal
            product={selectedProduct}
            isOpen={Boolean(selectedProduct)}
            onClose={handleCloseDetail}
          />,
          document.body
        )}
    </>
  );
};

export default CatalogoEspecialTemporada;
